import { clsx } from "clsx";
import { ReactSVG } from "react-svg";

import { useUIConfig } from "../../../config/uiConfigContext";

import styles from "./RVIcon.module.scss";

/**
 * Icon variant options for RVIcon.
 */
export const RVIconVariantOptions = {
    DEFAULT: "",
    CIRCLE: "circle",
    LARGE_CIRCLE: "large-circle",
} as const;

/**
 * Icon size options for RVIcon.
 */
export const RVIconSizeOptions = {
    BUTTON: "button",
    LARGE_BUTTON: "large-button",
    SMALL: "small",
    XSMALL: "xsmall",
    XXSMALL: "xxsmall",
    MEDIUM: "medium",
    DEFAULT: "",
    LARGE: "large",
    AVATAR: "avatar",
} as const;

/**
 * Type for icon variants.
 */
export type RVIconVariants = (typeof RVIconVariantOptions)[keyof typeof RVIconVariantOptions];

/**
 * Type for icon sizes.
 */
export type RVIconSizes = (typeof RVIconSizeOptions)[keyof typeof RVIconSizeOptions];

/**
 * Props for `RVIcon`.
 */
export type RVIconProps = {
    /** Name of the icon to render. */
    icon: string;
    /** Icon variant style. */
    variant?: RVIconVariants;
    /** Icon size. */
    size?: RVIconSizes;
    /** Icon color that maps to `$standard-colors` in `colors.scss`. */
    color?: string;
    /** Background color that maps to `$standard-colors` in `colors.scss`. */
    bgColor?: string;
    /** Additional className(s). */
    className?: string;
    /** Ref for the SVG element. */
    innerRef?: any;
    [props: string]: any;
};

export function RVIcon({
    icon,
    variant = RVIconVariantOptions.DEFAULT,
    size = RVIconSizeOptions.DEFAULT,
    color = "",
    bgColor = "",
    className = "",
    innerRef,
    // passed in by some 3rd parties, omit this from the ...props passed along to the object
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, react/prop-types
    iconClass,
    ...props
}: RVIconProps): JSX.Element {
    /**
     * Renders an SVG icon with styling options.
     */
    const { iconPath, getLogger } = useUIConfig();
    const logger = getLogger("RVIcon");
    const iconSrc = iconPath(icon);

    if (logger.mock) {
        // eslint-disable-next-line no-console
        console.error(`RVIcon using ${icon} must be used within a UIConfigProvider`);
    }

    return (
        <ReactSVG
            src={iconSrc}
            className={clsx(styles.icon, className, {
                [styles[color]]: !!color,
                [styles[`bg-${bgColor}`]]: !!bgColor,
                [styles[size]]: !!size,
                [styles[variant]]: !!variant,
            })}
            ref={innerRef}
            {...props}
        />
    );
}
